import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";
import {
  assertRuntimeCapability,
  RUNTIME_CAPABILITIES,
} from "../local/runtimeEnvironment";

export const MERCADO_PAGO_CONNECT_FEEDBACK_KEY =
  "mozzoapp:mercado-pago-connect-feedback";

const OAUTH_START_FUNCTION = "mp-oauth-start";
const OAUTH_CALLBACK_FUNCTION = "mp-oauth-callback";
const OAUTH_DISCONNECT_FUNCTION = "mp-oauth-disconnect";

const toSafeText = (value, maxLength = 400) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const sanitizePaymentAccountReference = (value) => {
  const normalized = toSafeText(value, 160);
  return /^APP_USR-/i.test(normalized) ? "" : normalized;
};

const isBrowser = typeof window !== "undefined";

const setFeedback = (payload) => {
  if (!isBrowser) {
    return;
  }

  try {
    window.sessionStorage.setItem(
      MERCADO_PAGO_CONNECT_FEEDBACK_KEY,
      JSON.stringify({
        ...payload,
        createdAt: new Date().toISOString(),
      })
    );
  } catch (error) {
    // Si falla el storage no frenamos el flujo.
  }
};

export const popMercadoPagoConnectFeedback = () => {
  if (!isBrowser) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(MERCADO_PAGO_CONNECT_FEEDBACK_KEY);

    if (!raw) {
      return null;
    }

    window.sessionStorage.removeItem(MERCADO_PAGO_CONNECT_FEEDBACK_KEY);
    const parsed = JSON.parse(raw);

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return {
      type: parsed.type === "error" ? "error" : "success",
      message: toSafeText(parsed.message, 240),
      branchId: toSafeText(parsed.branchId, 120),
      paymentProvider: toSafeText(parsed.paymentProvider, 40),
      paymentProviderStatus: toSafeText(parsed.paymentProviderStatus, 40),
      paymentAccountReference: sanitizePaymentAccountReference(
        parsed.paymentAccountReference
      ),
    };
  } catch (error) {
    return null;
  }
};

const buildMissingBackendMessage = (actionLabel) =>
  `Todavia no esta desplegada la funcion server-side para ${actionLabel}. La UI ya esta lista, pero falta publicar el backend de Mercado Pago.`;

const normalizeInvokeError = (error, fallbackMessage, actionLabel) => {
  const message = toSafeText(error?.message, 240);

  if (!message) {
    return fallbackMessage;
  }

  const lowered = message.toLowerCase();

  if (
    lowered.includes("failed to fetch") ||
    lowered.includes("networkerror") ||
    lowered.includes("functions fetch failed")
  ) {
    return `${fallbackMessage} Revisa la conexion o que la funcion ${actionLabel} este desplegada.`;
  }

  if (
    lowered.includes("not found") ||
    lowered.includes("404") ||
    lowered.includes("no route matched")
  ) {
    return buildMissingBackendMessage(actionLabel);
  }

  return message;
};

export const startMercadoPagoConnection = async ({
  branchId,
  branchName = "",
  returnToPath = "/app/restaurante",
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS);

  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos iniciar la conexion con Mercado Pago."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);

  if (!normalizedBranchId) {
    throw new Error(
      "No encontramos la sucursal activa para iniciar la conexion con Mercado Pago."
    );
  }

  const redirectUrl = isBrowser
    ? `${window.location.origin}/app/cobros/mercado-pago/callback`
    : "";

  const { data, error } = await supabase.functions.invoke(OAUTH_START_FUNCTION, {
    body: {
      branchId: normalizedBranchId,
      branchName: toSafeText(branchName, 120),
      provider: "mercado_pago",
      redirectUrl,
      returnToPath: toSafeText(returnToPath, 240) || "/app/restaurante",
    },
  });

  if (error) {
    throw new Error(
      normalizeInvokeError(
        error,
        "No pudimos iniciar la conexion con Mercado Pago.",
        OAUTH_START_FUNCTION
      )
    );
  }

  const authorizationUrl = toSafeText(
    data?.authorizationUrl || data?.oauthUrl,
    1400
  );

  if (!authorizationUrl) {
    throw new Error(
      "Mercado Pago no devolvio la URL de autorizacion para conectar la cuenta."
    );
  }

  return {
    authorizationUrl,
    branchId: normalizedBranchId,
    stateToken: toSafeText(data?.stateToken, 160),
  };
};

export const completeMercadoPagoConnection = async ({
  code,
  state,
  errorCode = "",
  errorDescription = "",
}) => {
  assertRuntimeCapability(RUNTIME_CAPABILITIES.PAYMENT_OPERATIONS);

  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos completar la conexion con Mercado Pago."
    );
  }

  if (errorCode) {
    throw new Error(
      errorDescription ||
        `Mercado Pago devolvio un error al conectar la cuenta: ${errorCode}.`
    );
  }

  const normalizedCode = toSafeText(code, 240);
  const normalizedState = toSafeText(state, 240);

  if (!normalizedCode || !normalizedState) {
    throw new Error(
      "La respuesta de Mercado Pago llego incompleta. Falta el codigo o el estado de autorizacion."
    );
  }

  const { data, error } = await supabase.functions.invoke(
    OAUTH_CALLBACK_FUNCTION,
    {
      body: {
        provider: "mercado_pago",
        code: normalizedCode,
        state: normalizedState,
      },
    }
  );

  if (error) {
    throw new Error(
      normalizeInvokeError(
        error,
        "No pudimos completar la conexion con Mercado Pago.",
        OAUTH_CALLBACK_FUNCTION
      )
    );
  }

  return {
    branchId: toSafeText(data?.branchId, 120),
    message: toSafeText(
      data?.message ||
        "La cuenta de Mercado Pago quedo conectada a MozzoApp para cobrar en Menu QR y kiosk.",
      240
    ),
    paymentProviderStatus: toSafeText(data?.paymentProviderStatus, 40),
    paymentAccountReference: sanitizePaymentAccountReference(
      data?.paymentAccountReference
    ),
  };
};

export const disconnectMercadoPagoConnection = async ({
  branchId,
  provider = "mercado_pago",
}) => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos desenlazar la cuenta de Mercado Pago."
    );
  }

  const normalizedBranchId = toSafeText(branchId, 120);

  if (!normalizedBranchId) {
    throw new Error(
      "No encontramos la sucursal activa para desenlazar la cuenta de Mercado Pago."
    );
  }

  const { data, error } = await supabase.functions.invoke(
    OAUTH_DISCONNECT_FUNCTION,
    {
      body: {
        branchId: normalizedBranchId,
        provider: toSafeText(provider, 40) || "mercado_pago",
      },
    }
  );

  if (error) {
    throw new Error(
      normalizeInvokeError(
        error,
        "No pudimos desenlazar la cuenta de Mercado Pago.",
        OAUTH_DISCONNECT_FUNCTION
      )
    );
  }

  return {
    branchId: toSafeText(data?.branchId, 120),
    message: toSafeText(
      data?.message ||
        "La cuenta de Mercado Pago quedo desenlazada de MozzoApp.",
      240
    ),
    paymentProviderStatus:
      toSafeText(data?.paymentProviderStatus, 40) || "not_configured",
    paymentAccountReference: sanitizePaymentAccountReference(
      data?.paymentAccountReference
    ),
  };
};

export const rememberMercadoPagoConnectSuccess = (payload) => {
  setFeedback({
    type: "success",
    message:
      toSafeText(payload?.message, 240) ||
      "La cuenta de Mercado Pago quedo conectada a MozzoApp.",
    branchId: toSafeText(payload?.branchId, 120),
    paymentProvider: "mercado_pago",
    paymentProviderStatus:
      toSafeText(payload?.paymentProviderStatus, 40) || "connected",
    paymentAccountReference: sanitizePaymentAccountReference(
      payload?.paymentAccountReference
    ),
  });
};

export const rememberMercadoPagoConnectError = (message, branchId = "") => {
  setFeedback({
    type: "error",
    message:
      toSafeText(message, 240) ||
      "No pudimos conectar la cuenta de Mercado Pago.",
    branchId: toSafeText(branchId, 120),
  });
};
